import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { t } from 'i18next';
import { FC, useCallback } from 'react';
import { AppState } from '../../AppState';
import historyIcon from '../../assets/bot-icons/history-btn-icon.svg';
import newChatIcon from '../../assets/bot-icons/new-chat-btn-icon.svg';
import { useChat } from '../../libs/hooks/useChat';
import { useRequestNewChat } from '../../libs/hooks/useRequestNewChat';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { FeedbackButton } from '../shared/FeedbackButton';
import { AgentConfigPanel } from './AgentConfigPanel';
import { EscalateToHumanButton } from './EscalateToHumanButton';

const useClasses = makeStyles({
    roomHeader: {
        display: 'grid',
        gridTemplateColumns: '3fr 2fr',
        width: '100%',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        paddingBottom: '10px',
        backgroundColor: tokens.colorNeutralBackground3,
        alignItems: 'center',
        '& > *': {
            marginTop: '10px',
            marginLeft: '10px',
        },
        '& > a': {
            justifySelf: 'end',
            textDecoration: 'none',
            color: tokens.colorNeutralForeground1,
            marginRight: '10px',
        },
    },
    btnGroup: {
        '& > button:last-child': {
            marginLeft: '10px',
        },
    },
    btn: {
        backgroundColor: tokens.colorNeutralBackground1,
        cursor: 'pointer',
    },
    'btn-history': {
        textAlign: 'right',
        backgroundImage: `url(${historyIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: '18%',
        backgroundPositionY: '50%',
    },
    'btn-new-chat': {
        textAlign: 'right',
        backgroundImage: `url(${newChatIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: '9%',
        backgroundPositionY: '50%',
        margin: '15px 5px',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '5px',
        padding: '5px 15px 5px 35px',
    },
    'btn-feedback': {
        right: '10px',
        position: 'absolute',
        margin: '15px 5px',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '5px',
        padding: '5px 15px',
        width: '100px',
    },
});

interface IChatRoomHeaderProps {
    setAppState: React.Dispatch<React.SetStateAction<AppState>>;
}

export const ChatRoomHeader: FC<IChatRoomHeaderProps> = ({ setAppState }) => {
    const classes = useClasses();
    const chat = useChat();
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const featureFlags = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags);

    const requestNewChat = useRequestNewChat();
    const onNewChat = useCallback(() => {
        requestNewChat(() =>
            chat
                .createChat(hostContext)
                .then(() => {
                    setAppState(AppState.SettingUserInfo);
                })
                .catch(() => {})
                .finally(() => {}),
        );
    }, [requestNewChat, chat, setAppState, hostContext]);

    return (
        <div className={classes.roomHeader}>
            <div className={classes.btnGroup}>
                <button
                    className={mergeClasses(classes.btn, classes['btn-new-chat'])}
                    onClick={onNewChat}
                    data-testid="new-chat-button"
                >
                    {t('app.newChat')}
                </button>
                {featureFlags.EnableAiProviderSelection && (
                    <AgentConfigPanel isVisible={featureFlags.EnableAiProviderSelection} />
                )}
                <EscalateToHumanButton />
                <FeedbackButton />
            </div>
        </div>
    );
};
